import React, { useState } from 'react';
import { Clock, Play, Pause, Plus, ChevronDown } from 'lucide-react';

interface Registro {
  id: string; nombre: string; obra: string; fecha: string;
  entrada: string; salida: string | null; horas: number | null; estado: 'activo' | 'cerrado';
}

const HOY = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
const AYER = new Date(Date.now() - 86400000).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });

const DEMO_REGISTROS: Registro[] = [
  { id: '1', nombre: 'Diego Vázquez', obra: 'Construcción Chalet Goya', fecha: HOY, entrada: '07:30', salida: null, horas: null, estado: 'activo' },
  { id: '2', nombre: 'Paula Sánchez', obra: 'Reforma Oficinas Paseo del Prado', fecha: HOY, entrada: '08:00', salida: null, horas: null, estado: 'activo' },
  { id: '3', nombre: 'Carlos Ruiz', obra: 'Construcción Chalet Goya', fecha: AYER, entrada: '07:15', salida: '16:00', horas: 8.75, estado: 'cerrado' },
  { id: '4', nombre: 'Diego Vázquez', obra: 'Reforma Oficinas Paseo del Prado', fecha: AYER, entrada: '08:30', salida: '18:00', horas: 9.5, estado: 'cerrado' },
  { id: '5', nombre: 'Paula Sánchez', obra: 'Reforma Oficinas Paseo del Prado', fecha: AYER, entrada: '08:00', salida: '17:30', horas: 9.5, estado: 'cerrado' },
  { id: '6', nombre: 'Andrés López', obra: 'Reforma Oficinas Paseo del Prado', fecha: AYER, entrada: '09:00', salida: '14:00', horas: 5, estado: 'cerrado' },
];

export default function ControlHorarioPage() {
  const [registros, setRegistros] = useState(DEMO_REGISTROS);
  const activos = registros.filter(r => r.estado === 'activo');
  const totalHoy = registros.filter(r => r.fecha === HOY && r.horas).reduce((s, r) => s + (r.horas ?? 0), 0);
  const totalSemana = registros.reduce((s, r) => s + (r.horas ?? 0), 0);

  const fichar = (id: string) => {
    const now = new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    setRegistros(prev => prev.map(r => {
      if (r.id !== id) return r;
      const [h, m] = r.entrada.split(':').map(Number);
      const [nh, nm] = now.split(':').map(Number);
      const horas = Math.round(((nh * 60 + nm) - (h * 60 + m)) / 60 * 10) / 10;
      return { ...r, salida: now, horas, estado: 'cerrado' };
    }));
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Control Horario</h1>
          <p className="text-sm text-gray-500 mt-0.5">{activos.length} trabajadores activos ahora</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-black rounded-xl font-semibold text-sm hover:bg-orange-400 transition-colors">
          <Plus size={16} /> Nuevo registro
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Activos ahora</div>
          <div className="text-3xl font-bold text-green-400">{activos.length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Horas hoy</div>
          <div className="text-3xl font-bold">{totalHoy.toFixed(1)}h</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Horas semana</div>
          <div className="text-3xl font-bold">{totalSemana.toFixed(1)}h</div>
        </div>
      </div>

      {activos.length > 0 && (
        <div className="mb-6">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">En obra ahora</div>
          <div className="space-y-2">
            {activos.map(r => (
              <div key={r.id} className="bg-green-500/10 border border-green-500/20 rounded-xl p-3 flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" />
                <div className="flex-1">
                  <span className="font-semibold text-sm">{r.nombre}</span>
                  <span className="text-gray-500 text-xs ml-2">· {r.obra.slice(0, 30)}</span>
                </div>
                <span className="text-xs text-gray-400">Desde {r.entrada}</span>
                <button onClick={() => fichar(r.id)} className="flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs font-semibold hover:bg-red-500/30 transition-colors">
                  <Pause size={12} /> Fichar salida
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Historial</div>
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Trabajador</th>
              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Obra</th>
              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Fecha</th>
              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Entrada</th>
              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Salida</th>
              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Horas</th>
            </tr>
          </thead>
          <tbody>
            {registros.filter(r => r.estado === 'cerrado').map(r => (
              <tr key={r.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                <td className="p-3 font-medium">{r.nombre}</td>
                <td className="p-3 text-gray-500 truncate max-w-[200px]">{r.obra.slice(0, 28)}</td>
                <td className="p-3 text-gray-400 text-xs">{r.fecha}</td>
                <td className="p-3">{r.entrada}</td>
                <td className="p-3">{r.salida}</td>
                <td className="p-3 font-bold text-orange-400">{r.horas}h</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
